import { createSlice } from '@reduxjs/toolkit';

/** A chat for the panel to show: `key` remounts the conversation per chat (eve reads its session options once). */
function nextChat(
  state,
  {
    id = null,
    summary = null,
    resume = false,
    previous = null,
    news = false,
    chosen = false,
    restored = false,
    afterEnded = false,
  } = {},
) {
  state.chatSeq += 1;

  return {
    key: `chat:${state.chatSeq}`,
    id,
    summary,
    resume,
    previous,
    ...(news ? { news: true } : {}),
    ...(chosen ? { chosen: true } : {}),
    ...(restored ? { restored: true } : {}),
    ...(afterEnded ? { afterEnded: true } : {}),
  };
}

// The platform agent's panel. Not persisted: the chats live in the agent (contracts §6).
// - `open`: whether the panel is open.
// - `restaurant`: the restaurant the panel's chat belongs to ({ subject, name }; subject as platformAgentSubjectKey).
//   Opening another restaurant starts a new chat there; closing and reopening the panel keeps the chat.
// - `chat`: the chat the panel shows. `id` is its eve session (null until the first message or New chat creates one),
//   `summary` its ChatSummary when it came from Chats (§6: who started it, read only), `resume` whether it has a
//   history to replay, `previous` the restaurant opened before, for the new chat line. `news` when the panel opened it
//   by itself for the button's news, `restored` when it opened it again after a reload (QA20), `ended` once eve said
//   its session can't go on (QA42: read only, New chat), `afterEnded` on the chat the panel started by itself for a
//   message sent into a chat that had ended (QL40), `chosen` when the person asked for exactly this chat elsewhere
//   (the Amigo log's Open the chat): the button's news doesn't replace it on that open (QA43); closing the panel drops
//   the mark.
// - `chats`: eve's session cursor and the stream events each chat rendered, by session id, kept while the panel is
//   closed or shows another chat, so it shows again and follows on from there (usePlatformAgentChat), and the files
//   its messages carried by eve's message id (`uploads`, contracts §8; eve's stream doesn't carry them).
// - `locations`: the location choice per subject, a location id or null for all; missing until the person picks
//   one, so the panel starts on what the page shows (platformAgentLocationChoice).
// - `pending`: a chat opened elsewhere (the Amigo log, D8) for a restaurant the panel isn't on yet ({ subject,
//   summary }): showRestaurant opens it there instead of a new chat once the dashboard has switched to it.
// - `bottomBar`: the height of a bar fixed to the page's bottom (the onboarding wizard's), 0 without one: the
//   bottom-right buttons (Ask Amigo, Intercom) sit above it. Set by useBottomBar.
// - `votes`: Helpful? (D10) per chat id and answer or card (its eve message id or change set id): true or false.
export default createSlice({
  name: 'platformAgent',
  initialState: {
    open: false,
    restaurant: null,
    chatSeq: 0,
    chat: { key: 'chat:0', id: null, summary: null, resume: false, previous: null },
    chats: {},
    locations: {},
    pending: null,
    bottomBar: 0,
    votes: {},
  },
  reducers: {
    open: (state) => {
      state.open = true;
    },
    close: (state) => {
      state.open = false;
      // A chat opened from the Amigo log is protected from the news only on that open (QA43): the next open goes to
      // the news again.
      delete state.chat.chosen;
    },
    setBottomBar: (state, action) => {
      state.bottomBar = action.payload > 0 ? Math.round(action.payload) : 0;
    },
    /** The restaurant in the dashboard's selector: another one than the chat's starts a new chat there. */
    showRestaurant: (state, action) => {
      const { subject, name = null } = action.payload ?? {};

      if (!subject) {
        return;
      }

      if (state.restaurant?.subject === subject) {
        state.restaurant.name = name;

        return;
      }

      const previous = state.restaurant?.name ?? null;
      state.restaurant = { subject, name };

      if (state.pending?.subject === subject) {
        const { summary } = state.pending;
        state.pending = null;
        state.chat = nextChat(state, { id: summary.id, summary, resume: true, chosen: true });

        return;
      }

      state.chat = nextChat(state, { previous });
    },
    /** New chat: an empty chat, with its eve session when the agent made one (POST /chats), else on the first message. */
    startChat: (state, action) => {
      const summary = action.payload?.summary ?? null;
      state.chat = nextChat(state, {
        id: summary?.id ?? null,
        summary,
        afterEnded: action.payload?.afterEnded === true,
      });
    },
    /**
     * A chat picked from Chats, or opened by the panel for the button's news (`news`) or again after a reload
     * (`restored`, QA20): its history replays. The open one stays as it is.
     */
    openChat: (state, action) => {
      const summary = action.payload?.summary;

      if (!summary?.id) {
        return;
      }

      if (state.chat.id === summary.id) {
        state.chat.summary = summary;

        return;
      }

      state.chat = nextChat(state, {
        id: summary.id,
        summary,
        resume: true,
        news: action.payload?.news === true,
        restored: action.payload?.restored === true,
      });
    },
    /**
     * eve said the session of the chat with key `chat` can't go on (QA42): it shows read only from the agent's archive,
     * with New chat. One the panel opened by itself (for the news, or again after a reload), untouched since, gives way
     * to an empty chat instead.
     */
    chatEnded: (state, action) => {
      if (!action.payload?.chat || state.chat.key !== action.payload.chat) {
        return;
      }

      if ((state.chat.news || state.chat.restored) && !state.chat.used) {
        state.chat = nextChat(state);

        return;
      }

      state.chat.ended = true;
    },
    /**
     * A chat opened from the Amigo log (D8) on the restaurant `subject` (`type:id`): the panel opens and shows it, at
     * once when it is on that restaurant, else once the dashboard has switched there (showRestaurant).
     */
    openChatOn: (state, action) => {
      const { subject, summary } = action.payload ?? {};

      if (!subject || !summary?.id) {
        return;
      }

      state.open = true;

      if (state.restaurant?.subject !== subject) {
        state.pending = { subject, summary };

        return;
      }

      state.pending = null;

      if (state.chat.id === summary.id) {
        state.chat.summary = summary;
        state.chat.chosen = true;

        return;
      }

      state.chat = nextChat(state, { id: summary.id, summary, resume: true, chosen: true });
    },
    /** The dashboard couldn't switch to the pending chat's restaurant: the panel stays on its own. */
    dropPending: (state) => {
      state.pending = null;
    },
    /**
     * The person did something in the chat with key `chat`: typed, sent a message, answered a question or a card,
     * tried again or stopped.
     */
    chatUsed: (state, action) => {
      if (action.payload?.chat && state.chat.key === action.payload.chat) {
        state.chat.used = true;
      }
    },
    /** eve created the session of the chat with key `chat` (its first message). */
    chatStarted: (state, action) => {
      const { chat, id } = action.payload ?? {};

      if (id && state.chat.key === chat && !state.chat.id) {
        state.chat.id = id;
      }
    },
    /**
     * The agent made the chat with key `chat` before its first message (POST /chats, for a file, D6): like New chat's
     * startChat({ summary }) its id is the new eve session with nothing to replay, but on the same chat, so the files
     * in its message box stay. `created` mounts the conversation again on that session; the first message goes there.
     */
    chatCreated: (state, action) => {
      const { chat, summary } = action.payload ?? {};

      if (!summary?.id || state.chat.key !== chat || state.chat.id) {
        return;
      }

      state.chat.id = summary.id;
      state.chat.summary = summary;
      state.chat.resume = false;
      state.chat.created = true;
    },
    /** What eve rendered of a chat, when its conversation unmounts (the panel closes, or shows another chat). */
    saveChat: (state, action) => {
      const { chat, session, events, uploads = {} } = action.payload ?? {};

      if (!session?.sessionId) {
        return;
      }

      state.chats[session.sessionId] = {
        session: { sessionId: session.sessionId, streamIndex: session.streamIndex },
        events,
        uploads,
      };

      if (chat && state.chat.key === chat && !state.chat.id) {
        state.chat.id = session.sessionId;
      }
    },
    /** The location bar: a location id, or null for all locations. */
    setLocation: (state, action) => {
      const { subject, location = null } = action.payload ?? {};

      if (subject) {
        state.locations[subject] = location;
      }
    },
    /** Helpful? on an answer or a card of a chat: one vote, true or false. */
    voted: (state, action) => {
      const { chat, message, helpful } = action.payload ?? {};

      if (chat && message) {
        state.votes[chat] = { ...state.votes[chat], [message]: Boolean(helpful) };
      }
    },
    /** A vote taken back (B36), or one that didn't reach the agent: Helpful? asks again. */
    unvoted: (state, action) => {
      const { chat, message } = action.payload ?? {};

      if (chat && message && state.votes[chat]) {
        delete state.votes[chat][message];
      }
    },
  },
});
